import { defineComponent as _defineComponent } from 'vue'
import ScaleOut from "@/components/scale-out-component.vue";
import BreadCrumbNavigation from "@/components/bread-crumb-component.vue";
import DocumentContent from "@/components/courseworks/document-content.vue";
import GrantSpecialCourseWorkSubmissionDialog from "@/components/dialogs/grant-special-corse-work-submission-dialog.vue";
import debounce from "debounce";

import { format, parseISO } from "date-fns";
//@ts-ignore
import getMonthName from "@/helpers/getMonthName";

import { onMounted, Ref, ref } from "vue";
import useHttpRequest from "@/composables/use-http-request";
import { useRoute } from "vue-router/composables";

import CourseWork from "@/interfaces/courseWork.interface";
import CourseWorkIntake from "@/interfaces/courseWorkIntake.interface";
import { useParamsSync } from "@/composables/use-params-sync";
import getStudentFullName from "@/helpers/getStudentFullName";
import StudentCourseWorkSubmission from "@/interfaces/studentCourseWorkSubmission.interface";

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export default /*#__PURE__*/_defineComponent({
  __name: 'course-work-details',
  setup(__props) {

let scope: { row: StudentCourseWorkSubmission; $index: number };

const route = useRoute();

const loading = ref(false);
const loadingError = ref(false);
const submitting = ref(false);
const showDialog = ref(false);

const activeName = ref("questionPaper");
const course_work: Ref<CourseWork> = ref({} as CourseWork);
const studentId = ref("");
const batches = ref<CourseWorkIntake[]>([]);

const submissions = ref<StudentCourseWorkSubmission[]>([]);
const searchValue = ref("");
const totalSubmissions = ref(0);
const pageSize = ref(10);
const currentPage = ref(1);
const tableProps = {
  border: true,
  stripe: true,
};

const toMonthName = (monthNum: number) => {
  return getMonthName(monthNum);
};

const getInitials = (nameString: string) => {
  var names = nameString.split(" "),
    initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

const formattedDate = (dateIso: string | null) => {
  if (!dateIso) return "N/A";
  return format(parseISO(dateIso), "dd MMM yyyy hh:mma");
};

const getCourseWork = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    course_work: CourseWork;
  }>({
    method: "GET",
    url: `course-work/fetch-one/${route.params.courseWorkId}`,
    loadingProperty: loading,
    showSuccessMessage: false,
  });

  if (!request) return;

  if (
    request.success &&
    request.message == "COURSE_WORK FETCHED SUCCESSFULLY"
  ) {
    course_work.value = request.course_work;
    batches.value = request.course_work.course_work_intakes;
  }
};

onMounted(() => {
  useParamsSync(
    { key: "ps", value: pageSize },
    { key: "p", value: currentPage },
    { key: "s", value: searchValue }
  );

  getCourseWork();
});

const getSubmissions = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    totalSubmissions: number;
    submissions: StudentCourseWorkSubmission[];
  }>({
    method: "GET",
    url: `course-work/fetch-submissions/${route.params.courseWorkId}?page=${currentPage.value}&pageSize=${pageSize.value}&search=${searchValue.value}`,
    loadingProperty: submitting,
    showSuccessMessage: false,
  });

  if (!request) return;

  if (
    request.success &&
    request.message == "Course Work submissions retrieved successfully"
  ) {
    submissions.value = request.submissions;
    totalSubmissions.value = request.totalSubmissions;
  }
};

const toggleResubmissionAllowed = async (courseWorkSubmissionId: string) => {
  const request = await useHttpRequest<{ success: boolean; message: string }>({
    method: "PATCH",
    url: `course-work/toggle-resubmission-allowed/${courseWorkSubmissionId}`,
    loadingProperty: null,
    showSuccessMessage: true,
    showFullScreenLoader: true,
  });

  if (!request) return;
  if (
    request.success &&
    request.message == "Resubmission Permission updated Successfully"
  ) {
    getSubmissions();
  }
};

const onSearch = debounce(function () {
  getSubmissions();
}, 500);

const onTableChange = (queryInfo: any) => {
  if (queryInfo) {
    if (queryInfo.type == "page" || queryInfo.type == "size") {
      currentPage.value = queryInfo.page;
      pageSize.value = queryInfo.pageSize;
      getSubmissions();
    }
  }
};

const getIntakeInfo = (courseIntakeBatchCourseWorkId: string) => {
  const intake = course_work.value?.course_work_intakes.find(
    (item: CourseWorkIntake) =>
      item.course_intake_batch_course_work_id === courseIntakeBatchCourseWorkId
  );

  if (!intake) {
    return "Intake not found";
  }

  return `${intake.course_intake_batch.course.course_code} ${
    intake.course_intake_batch.year
  } ${toMonthName(intake.course_intake_batch.month).toUpperCase()} ${
    intake.course_intake_batch.cohort
  } (${intake.course_unit.course_unit_code})`;
};

const downloadOneStudentCourseWorkSubmission = async (
  studentCourseworkSubmissionId: string
) => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    path_url: string;
  }>({
    method: "GET",
    url: `course-work/download-one-student-submission/${studentCourseworkSubmissionId}`,
    loadingProperty: submitting,
    showSuccessMessage: true,
  });
  if (!request) return;
  if (
    request.success &&
    request.message == "Student Course work submission retrieved successfully"
  ) {
    const link = document.createElement("a");
    link.href = request.path_url;
    link.click();
  }
};

const downloadStudentSubmissions = async (
  course_intake_batch_course_work_id: string | null
) => {
  let queryParam = "";
  if (course_intake_batch_course_work_id) {
    queryParam = `?course_intake_batch_course_work_id=${course_intake_batch_course_work_id}`;
  }

  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    link: string;
  }>({
    method: "GET",
    url: `course-work/download-students-submissions/${route.params.courseWorkId}${queryParam}`,
    loadingProperty: submitting,
    showSuccessMessage: true,
  });
  if (!request) return;
  if (
    request.success &&
    request.message == "STUDENT SUBMISSIONS ZIP GENERATED"
  ) {
    const link = document.createElement("a");
    link.href = request.link;
    link.click();
  }
};

const onTabClick = (tab: any) => {
  if (tab.name === "submissions") {
    getSubmissions();
  }
};

const showGrantTimeDialog = (id: string) => {
  studentId.value = id;
  showDialog.value = true;
};

const closeGrantSpecialSubmissionDialog = () => {
  showDialog.value = false;
};

return { __sfc: true,scope, route, loading, loadingError, submitting, showDialog, activeName, course_work, studentId, batches, submissions, searchValue, totalSubmissions, pageSize, currentPage, tableProps, toMonthName, getInitials, formattedDate, getCourseWork, getSubmissions, toggleResubmissionAllowed, onSearch, onTableChange, getIntakeInfo, downloadOneStudentCourseWorkSubmission, downloadStudentSubmissions, onTabClick, showGrantTimeDialog, closeGrantSpecialSubmissionDialog, ScaleOut, BreadCrumbNavigation, DocumentContent, GrantSpecialCourseWorkSubmissionDialog, getStudentFullName }
}

})